import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart2, TrendingUp, Eye, MousePointer, Users, Calendar, Globe } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ApplyOnlineAnalytics from '../components/ApplyOnlineAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsManager = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('components'); // 'components' or 'apply-online'
  const [summary, setSummary] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);
  const [expandedComponent, setExpandedComponent] = useState(null); // Track which component detail is expanded
  const detailsSectionRef = React.useRef(null); // Ref for scrolling to details

  useEffect(() => {
    fetchAnalytics();
  }, [selectedDays]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token'); // Fixed: was 'token', should be 'admin_token'
      
      if (!token) {
        console.error('No authentication token found');
        toast({
          title: "Authentication Required",
          description: "Please log in to view analytics.",
          variant: "destructive"
        });
        return;
      }
      
      // Fetch summary data
      const summaryRes = await axios.get(`${API}/analytics/admin/summary?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch component breakdown
      const breakdownRes = await axios.get(`${API}/analytics/admin/component-breakdown?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSummary(summaryRes.data.summary || []);
      setDateRange(summaryRes.data.date_range);
      setBreakdown(breakdownRes.data.breakdown || []);
      
      console.log('Analytics data loaded:', {
        summaryCount: summaryRes.data.summary?.length,
        breakdownCount: breakdownRes.data.breakdown?.length
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      if (error.response?.status === 401) {
        console.error('Authentication failed - please log in again');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getComponentIcon = (componentType) => {
    switch (componentType) {
      case 'service_card':
        return <BarChart2 size={20} />;
      case 'destination_card':
        return <TrendingUp size={20} />;
      case 'blog_card':
      case 'blog_detail':
        return <Eye size={20} />;
      case 'cta_button':
        return <MousePointer size={20} />;
      case 'form':
        return <Users size={20} />;
      default:
        return <BarChart2 size={20} />;
    }
  };

  const getComponentLabel = (componentType) => {
    const labels = {
      service_card: 'Services',
      destination_card: 'Top Destinations',
      blog_card: 'Blog Cards',
      blog_detail: 'Blog Details',
      cta_button: 'CTA Buttons',
      form: 'Forms',
      contact_form: 'Contact Form',
      announcement: 'Announcements'
    };
    return labels[componentType] || componentType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter out test components
  const filteredSummary = summary.filter(item => 
    !item.component_type.includes('test') && item.component_type !== 'test'
  );
  
  const filteredBreakdown = breakdown.filter(item => 
    !item.component_type.includes('test') && item.component_type !== 'test'
  );

  const handleCardClick = (componentType) => {
    const isExpanding = expandedComponent !== componentType;
    setExpandedComponent(expandedComponent === componentType ? null : componentType);
    
    // Scroll to details section if expanding
    if (isExpanding && detailsSectionRef.current) {
      setTimeout(() => {
        detailsSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' // Using 'start' with scroll-margin-top in CSS
        });
      }, 100); // Small delay to ensure DOM is updated
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="analytics-page-container">
        <div className="analytics-page-header">
          <div>
            <h1 className="heading-1">Performance Analytics</h1>
            <p className="body-large">Track user engagement across your website components</p>
          </div>
          
          {/* Date Range Filter - only show for components tab */}
          {activeTab === 'components' && (
            <div className="analytics-filters">
            <div className="filter-group">
              <label>Time Period:</label>
              <select 
                value={selectedDays} 
                onChange={(e) => setSelectedDays(Number(e.target.value))}
                className="filter-select"
              >
                <option value={1}>Last 24 Hours</option>
                <option value={7}>Last 7 Days</option>
                <option value={14}>Last 14 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={90}>Last 90 Days</option>
              </select>
            </div>
            {dateRange && (
              <div className="date-range-display">
                <Calendar size={16} />
                <span>{formatDate(dateRange.start)} - {formatDate(dateRange.end)}</span>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '24px' }}>
            <button
              onClick={() => setActiveTab('components')}
              style={{
                padding: '12px 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'components' ? '2px solid #A08EA8' : '2px solid transparent',
                color: activeTab === 'components' ? '#A08EA8' : '#6b7280',
                fontWeight: activeTab === 'components' ? '600' : '400',
                fontSize: '16px',
                cursor: 'pointer',
                marginBottom: '-2px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <BarChart2 size={18} />
              Component Analytics
            </button>
            <button
              onClick={() => setActiveTab('apply-online')}
              style={{
                padding: '12px 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'apply-online' ? '2px solid #A08EA8' : '2px solid transparent',
                color: activeTab === 'apply-online' ? '#A08EA8' : '#6b7280',
                fontWeight: activeTab === 'apply-online' ? '600' : '400',
                fontSize: '16px',
                cursor: 'pointer',
                marginBottom: '-2px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Globe size={18} />
              Apply Online Analytics
            </button>
          </div>
        </div>

      {/* Apply Online Analytics Tab */}
      {activeTab === 'apply-online' && (
        <ApplyOnlineAnalytics />
      )}

      {/* Component Analytics Tab */}
      {activeTab === 'components' && (
        <>
      {/* Summary Cards */}
      {filteredSummary.length > 0 ? (
        <div className="analytics-summary-grid">
          {filteredSummary.map((item) => (
          <div 
            key={item.component_type} 
            className={`analytics-summary-card ${expandedComponent === item.component_type ? 'active' : ''}`}
            onClick={() => handleCardClick(item.component_type)}
            style={{ cursor: 'pointer' }}
            title="Click to view detailed breakdown"
          >
            <div className="summary-card-header">
              <div className="summary-icon">
                {getComponentIcon(item.component_type)}
              </div>
              <h3 className="heading-3">{getComponentLabel(item.component_type)}</h3>
            </div>
            
            <div className="summary-metrics">
              <div className="metric">
                <Eye size={16} />
                <div>
                  <div className="metric-value">{item.total_views.toLocaleString()}</div>
                  <div className="metric-label">Views</div>
                </div>
              </div>
              
              <div className="metric">
                <MousePointer size={16} />
                <div>
                  <div className="metric-value">{item.total_clicks.toLocaleString()}</div>
                  <div className="metric-label">Clicks</div>
                </div>
              </div>
              
              <div className="metric">
                <Users size={16} />
                <div>
                  <div className="metric-value">{item.unique_sessions.toLocaleString()}</div>
                  <div className="metric-label">Sessions</div>
                </div>
              </div>
              
              <div className="metric">
                <TrendingUp size={16} />
                <div>
                  <div className="metric-value">{item.click_through_rate}%</div>
                  <div className="metric-label">CTR</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="empty-state">
          <p className="body-large">No analytics data available for the selected period.</p>
        </div>
      )}

      {/* Action Breakdown - Moved to top */}
      {filteredBreakdown.length > 0 && (
        <div className="analytics-table-section">
          <h2 className="heading-2">Action Breakdown by Component</h2>
          <div className="table-responsive">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Actions</th>
                  <th className="text-center">Total Events</th>
                </tr>
              </thead>
              <tbody>
                {filteredBreakdown.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="component-name">
                        {getComponentIcon(item.component_type)}
                        {getComponentLabel(item.component_type)}
                      </div>
                    </td>
                    <td>
                      <div className="action-tags">
                        {Object.entries(item.actions).map(([action, count]) => (
                          <span key={action} className="action-tag">
                            {action}: {count}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-center">
                      <strong>{item.total_events.toLocaleString()}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed Tables - Show only for expanded component */}
      {expandedComponent && (
        <div className="analytics-tables" ref={detailsSectionRef}>
          {filteredSummary
            .filter(item => item.component_type === expandedComponent)
            .map((item) => (
          <div key={item.component_type} className="analytics-table-section">
            <h2 className="heading-2">
              {getComponentLabel(item.component_type)} - Top Performers
            </h2>
            
            {item.top_items && item.top_items.length > 0 ? (
              <div className="table-responsive">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="text-center">Views</th>
                      <th className="text-center">Clicks</th>
                      <th className="text-center">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.top_items.map((topItem, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="item-name">{topItem.item_name}</div>
                          <div className="item-id">{topItem.item_id}</div>
                        </td>
                        <td className="text-center">{topItem.views.toLocaleString()}</td>
                        <td className="text-center">{topItem.clicks.toLocaleString()}</td>
                        <td className="text-center">
                          <span className={`ctr-badge ${topItem.ctr > 10 ? 'high' : topItem.ctr > 5 ? 'medium' : 'low'}`}>
                            {topItem.ctr.toFixed(2)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p className="body-medium">No data available for this component</p>
              </div>
            )}
          </div>
        ))}
      </div>
      )}
        </>
      )}
      </div>
    </div>
  );
};

export default AnalyticsManager;
